import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { attributesService } from 'quotes/services';
import { IAttributeCustomer } from 'quotes/types';

export const useSaveAttributes = (position: NotificationPosition = NotificationPosition.FORM) => {
  const { t } = useTranslation();

  // Prefix-match key: the details query key carries a `{ quoteId, isOrder? }` criteria object we
  // don't reconstruct here, so we invalidate every `['quote', ...]` query.
  const { errors } = useHandleErrors({
    success: { message: t('messageAttributesSaveSuccess') },
    loading: { message: t('messageAttributesSavingInProgress') },
    error: { title: t('errorTitleAttributesSave') },
    position,
    invalidateQueries: ['quote'],
  });

  const { mutateAsync, isLoading } = useMutation(attributesService.saveAttributes, { ...errors });

  return { saveAttributes: mutateAsync, isSaving: isLoading };
};

// No notification: the caller shows the BE message under the Customers field, since the step
// lives in drawers and wizards with different notification slots.
export const useGetProjectCustomers = (projectId: number | null) => {
  const { data, isFetching, error } = useQuery(
    ['attributesProjectCustomers', projectId],
    ({ signal }) => attributesService.getProjectCustomers(projectId, signal),
    { enabled: !!projectId, retry: false, refetchOnWindowFocus: false }
  );

  const errorMessage = error
    ? (error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? null
    : null;

  return {
    customers: data?.customers as IAttributeCustomer[] | undefined,
    isFetching,
    isError: !!error,
    errorMessage,
  };
};
